import React, { useState, useEffect, useMemo } from "react";
import {
  User,
  Mail,
  AtSign,
  Shield,
  KeyRound,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Save,
  Clock,
  Calendar,
  Lock,
  BadgeCheck,
  Check,
  X,
} from "lucide-react";
import { useAuth } from "../utils/AuthContext";
import { useToast } from "../utils/ToastContext";
import { authAPI } from "../utils/api";
import "./Profile.css";

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const toast = useToast();

  // Profile Form State
  const [profileForm, setProfileForm] = useState({
    fullName: "",
    username: "",
    email: "",
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState("");
  const [profileError, setProfileError] = useState("");

  // Password Form State
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Populate user data
  useEffect(() => {
    if (user) {
      setProfileForm({
        fullName: user.fullName || "",
        username: user.username || "",
        email: user.email || "",
      });
    }
  }, [user]);

  // Check if profile is modified
  const isProfileChanged = useMemo(() => {
    if (!user) return false;
    return (
      profileForm.fullName.trim() !== (user.fullName || "") ||
      profileForm.username.trim().toLowerCase() !==
        (user.username || "").toLowerCase() ||
      profileForm.email.trim().toLowerCase() !==
        (user.email || "").toLowerCase()
    );
  }, [profileForm, user]);

  // Password Strength Calculation
  const passwordStrength = useMemo(() => {
    const pwd = passwordForm.newPassword;
    if (!pwd) return { score: 0, label: "Enter password", class: "" };

    let score = 0;
    if (pwd.length >= 6) score += 1;
    if (pwd.length >= 10) score += 1;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;

    if (score <= 1) return { score: 1, label: "Weak", class: "active-weak" };
    if (score <= 3) return { score: 2, label: "Fair", class: "active-medium" };
    if (score === 4) return { score: 3, label: "Good", class: "active-strong" };
    return { score: 4, label: "Excellent", class: "active-strong" };
  }, [passwordForm.newPassword]);

  // Requirement checks
  const checks = {
    length: passwordForm.newPassword.length >= 6,
    hasNumber: /[0-9]/.test(passwordForm.newPassword),
    hasUpperLower:
      /[A-Z]/.test(passwordForm.newPassword) &&
      /[a-z]/.test(passwordForm.newPassword),
    matches:
      Boolean(passwordForm.newPassword) &&
      passwordForm.newPassword === passwordForm.confirmPassword,
  };

  // Profile update submit
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError("");
    setProfileSuccess("");

    if (!profileForm.fullName.trim()) {
      setProfileError("Full Name is required.");
      return;
    }
    if (!profileForm.username.trim()) {
      setProfileError("Username is required.");
      return;
    }
    if (!profileForm.email.trim() || !profileForm.email.includes("@")) {
      setProfileError("A valid email address is required.");
      return;
    }

    setProfileSaving(true);
    try {
      const response = await authAPI.updateProfile({
        fullName: profileForm.fullName.trim(),
        username: profileForm.username.trim(),
        email: profileForm.email.trim(),
      });

      const updatedUser = response.data?.data?.user;
      const newToken = response.data?.data?.token;

      if (updatedUser) {
        updateUser(updatedUser, newToken);
      }

      setProfileSuccess("Profile updated successfully!");
      toast("Profile updated successfully", "success");
      setTimeout(() => setProfileSuccess(""), 4000);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to update profile";
      setProfileError(message);
      toast(message, "error");
    } finally {
      setProfileSaving(false);
    }
  };

  // Password change submit
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (!passwordForm.currentPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError("Please enter a new password.");
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setPasswordSaving(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      });

      setPasswordSuccess("Password changed successfully!");
      toast("Password changed successfully", "success");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => setPasswordSuccess(""), 4000);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Failed to change password";
      setPasswordError(message);
      toast(message, "error");
    } finally {
      setPasswordSaving(false);
    }
  };

  const getRoleTitle = (role) => {
    switch (role) {
      case "superadmin":
        return "System Superadmin";
      case "admin":
        return "System Administrator";
      case "reviewer":
        return "Quality Reviewer";
      case "assessor":
      default:
        return "Lead Assessor";
    }
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "Recently";
    try {
      return new Date(dateVal).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Active";
    }
  };

  return (
    <div className="profile-page-container">
      {/* Header Banner */}
      <section className="profile-hero">
        <div className="profile-hero-left">
          <div className="profile-avatar-large">
            {(user?.fullName || "U").charAt(0).toUpperCase()}
          </div>
          <div className="profile-hero-details">
            <h1>{user?.fullName || "User Profile"}</h1>
            <div className="profile-hero-username">
              @{user?.username || "user"}
            </div>
            <div className="profile-hero-tags">
              <span className={`role-pill ${user?.role || "assessor"}`}>
                <Shield size={12} />
                {getRoleTitle(user?.role)}
              </span>
              <span className="status-pill-active">
                <span className="status-dot-pulse" />
                Account Active
              </span>
            </div>
          </div>
        </div>

        <div className="profile-hero-meta">
          <div className="hero-meta-block">
            <span className="hero-meta-label">
              <Clock
                size={11}
                style={{ display: "inline", marginRight: "4px" }}
              />
              Role
            </span>
            <span className="hero-meta-val">
              {user?.role?.toUpperCase() || "USER"}
            </span>
          </div>
          <div className="hero-meta-block">
            <span className="hero-meta-label">
              <Mail
                size={11}
                style={{ display: "inline", marginRight: "4px" }}
              />
              Email
            </span>
            <span className="hero-meta-val">{user?.email || "Not set"}</span>
          </div>
          <div className="hero-meta-block">
            <span className="hero-meta-label">
              <Calendar
                size={11}
                style={{ display: "inline", marginRight: "4px" }}
              />
              Member Since
            </span>
            <span className="hero-meta-val">{formatDate(user?.createdAt)}</span>
          </div>
        </div>
      </section>

      {/* Main Grid: Form Sections on Left, Info Cards on Right */}
      <div className="profile-grid">
        <div className="profile-forms-column">
          {/* 1. Profile Information Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div className="profile-card-title-group">
                <div className="profile-icon-wrapper blue">
                  <User size={18} />
                </div>
                <div>
                  <h2 className="profile-card-title">Personal Information</h2>
                  <p className="profile-card-subtitle">
                    Update your account details and contact information
                  </p>
                </div>
              </div>
            </div>

            <div className="profile-card-body">
              {profileSuccess && (
                <div className="profile-alert success">
                  <CheckCircle2 size={16} />
                  <span>{profileSuccess}</span>
                </div>
              )}

              {profileError && (
                <div className="profile-alert error">
                  <AlertCircle size={16} />
                  <span>{profileError}</span>
                </div>
              )}

              <form onSubmit={handleProfileSubmit}>
                <div className="profile-form-grid full-width">
                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="fullName">
                      Full Name
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <User size={16} />
                      </span>
                      <input
                        id="fullName"
                        type="text"
                        className="input-custom"
                        placeholder="e.g. Dr. John Doe"
                        value={profileForm.fullName}
                        onChange={(e) =>
                          setProfileForm({
                            ...profileForm,
                            fullName: e.target.value,
                          })
                        }
                        disabled={profileSaving}
                      />
                    </div>
                  </div>
                </div>

                <div className="profile-form-grid">
                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="username">
                      Username
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <AtSign size={16} />
                      </span>
                      <input
                        id="username"
                        type="text"
                        className="input-custom"
                        placeholder="username"
                        value={profileForm.username}
                        onChange={(e) =>
                          setProfileForm({
                            ...profileForm,
                            username: e.target.value.toLowerCase().trim(),
                          })
                        }
                        disabled={profileSaving}
                      />
                    </div>
                    <span className="input-hint">
                      Used for signing into your account
                    </span>
                  </div>

                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="email">
                      Email Address
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <Mail size={16} />
                      </span>
                      <input
                        id="email"
                        type="email"
                        className="input-custom"
                        placeholder="user@ahc.local"
                        value={profileForm.email}
                        onChange={(e) =>
                          setProfileForm({
                            ...profileForm,
                            email: e.target.value.trim(),
                          })
                        }
                        disabled
                      />
                    </div>
                    <span className="input-hint">
                      For system notifications and reporting
                    </span>
                  </div>
                </div>

                <div className="profile-form-grid">
                  <div className="form-group-custom">
                    <label className="form-label-custom">System Role</label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <Shield size={16} />
                      </span>
                      <input
                        type="text"
                        className="input-custom read-only"
                        value={getRoleTitle(user?.role)}
                        disabled
                      />
                    </div>
                    <span className="input-hint">
                      Role assigned by system administrators
                    </span>
                  </div>

                  <div className="form-group-custom">
                    <label className="form-label-custom">Account ID</label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <BadgeCheck size={16} />
                      </span>
                      <input
                        type="text"
                        className="input-custom read-only"
                        value={user?.id || user?._id || "Assigned"}
                        disabled
                      />
                    </div>
                    <span className="input-hint">Unique system identifier</span>
                  </div>
                </div>

                <div className="profile-actions">
                  <button
                    type="submit"
                    className="btn-profile-primary"
                    disabled={profileSaving || !isProfileChanged}
                  >
                    {profileSaving ? (
                      <>
                        <span className="spinner-inline" />
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Profile Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* 2. Security & Change Password Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <div className="profile-card-title-group">
                <div className="profile-icon-wrapper gold">
                  <KeyRound size={18} />
                </div>
                <div>
                  <h2 className="profile-card-title">Security & Password</h2>
                  <p className="profile-card-subtitle">
                    Manage your password and protect your account
                  </p>
                </div>
              </div>
            </div>

            <div className="profile-card-body">
              {passwordSuccess && (
                <div className="profile-alert success">
                  <CheckCircle2 size={16} />
                  <span>{passwordSuccess}</span>
                </div>
              )}

              {passwordError && (
                <div className="profile-alert error">
                  <AlertCircle size={16} />
                  <span>{passwordError}</span>
                </div>
              )}

              <form onSubmit={handlePasswordSubmit}>
                <div className="profile-form-grid full-width">
                  <div className="form-group-custom">
                    <label
                      className="form-label-custom"
                      htmlFor="currentPassword"
                    >
                      Current Password
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <Lock size={16} />
                      </span>
                      <input
                        id="currentPassword"
                        type={showCurrent ? "text" : "password"}
                        className="input-custom"
                        placeholder="Enter current password"
                        value={passwordForm.currentPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            currentPassword: e.target.value,
                          })
                        }
                        disabled={passwordSaving}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowCurrent(!showCurrent)}
                        aria-label="Toggle password visibility"
                      >
                        {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="profile-form-grid">
                  <div className="form-group-custom">
                    <label className="form-label-custom" htmlFor="newPassword">
                      New Password
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <KeyRound size={16} />
                      </span>
                      <input
                        id="newPassword"
                        type={showNew ? "text" : "password"}
                        className="input-custom"
                        placeholder="Minimum 6 characters"
                        value={passwordForm.newPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            newPassword: e.target.value,
                          })
                        }
                        disabled={passwordSaving}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowNew(!showNew)}
                        aria-label="Toggle password visibility"
                      >
                        {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group-custom">
                    <label
                      className="form-label-custom"
                      htmlFor="confirmPassword"
                    >
                      Confirm New Password
                    </label>
                    <div className="input-with-icon">
                      <span className="input-left-icon">
                        <KeyRound size={16} />
                      </span>
                      <input
                        id="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        className="input-custom"
                        placeholder="Re-enter new password"
                        value={passwordForm.confirmPassword}
                        onChange={(e) =>
                          setPasswordForm({
                            ...passwordForm,
                            confirmPassword: e.target.value,
                          })
                        }
                        disabled={passwordSaving}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowConfirm(!showConfirm)}
                        aria-label="Toggle password visibility"
                      >
                        {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Password strength indicators */}
                {passwordForm.newPassword && (
                  <div className="password-meter">
                    <div className="password-meter-header">
                      <span>Password Strength:</span>
                      <span style={{ fontWeight: 700 }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    <div className="strength-bars">
                      {[1, 2, 3, 4].map((step) => (
                        <div
                          key={step}
                          className={`strength-segment ${step <= passwordStrength.score
                            ? passwordStrength.class
                            : ""
                            }`}
                        />
                      ))}
                    </div>

                    <div className="password-checklist">
                      <div
                        className={`check-item ${checks.length ? "met" : ""}`}
                      >
                        {checks.length ? <Check size={13} /> : <X size={13} />}
                        <span>At least 6 characters</span>
                      </div>
                      <div
                        className={`check-item ${checks.hasNumber ? "met" : ""}`}
                      >
                        {checks.hasNumber ? (
                          <Check size={13} />
                        ) : (
                          <X size={13} />
                        )}
                        <span>Contains numbers (0-9)</span>
                      </div>
                      <div
                        className={`check-item ${checks.hasUpperLower ? "met" : ""
                          }`}
                      >
                        {checks.hasUpperLower ? (
                          <Check size={13} />
                        ) : (
                          <X size={13} />
                        )}
                        <span>Uppercase & lowercase letters</span>
                      </div>
                      <div
                        className={`check-item ${checks.matches ? "met" : ""}`}
                      >
                        {checks.matches ? <Check size={13} /> : <X size={13} />}
                        <span>Passwords match</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="profile-actions">
                  <button
                    type="submit"
                    className="btn-profile-primary btn-profile-gold"
                    disabled={
                      passwordSaving ||
                      !passwordForm.currentPassword ||
                      !passwordForm.newPassword ||
                      !checks.matches
                    }
                  >
                    {passwordSaving ? (
                      <>
                        <span className="spinner-inline" />
                        Updating Password...
                      </>
                    ) : (
                      <>
                        <KeyRound size={16} />
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar Info Panels */}
        <aside className="profile-sidebar-column">
          {/* Role & Permissions Card */}
          <div className="info-box">
            <h3 className="info-box-title">
              <Shield size={16} color="#1d4ed8" />
              Role Permissions
            </h3>
            <div className="permission-list">
              <div className="permission-item">
                <CheckCircle2 size={15} className="perm-icon" />
                <span>Perform and score PHC Capacity Assessments</span>
              </div>
              <div className="permission-item">
                <CheckCircle2 size={15} className="perm-icon" />
                <span>Upload and link verification evidence attachments</span>
              </div>
              <div className="permission-item">
                <CheckCircle2 size={15} className="perm-icon" />
                <span>Generate and export standard assessment reports</span>
              </div>
              {user?.role === "admin" && (
                <>
                  <div className="permission-item">
                    <CheckCircle2 size={15} className="perm-icon" />
                    <span>Manage user accounts and assign security roles</span>
                  </div>
                  <div className="permission-item">
                    <CheckCircle2 size={15} className="perm-icon" />
                    <span>Access institutional configuration & audit logs</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Session Details */}
          <div className="info-box">
            <h3 className="info-box-title">
              <Clock size={16} color="#d97706" />
              Session & Security
            </h3>
            <div className="account-summary-row">
              <span className="summary-label">Status</span>
              <span className="summary-val" style={{ color: "#16a34a" }}>
                Connected (Online)
              </span>
            </div>
            <div className="account-summary-row">
              <span className="summary-label">Auth Protocol</span>
              <span className="summary-val">HMAC-SHA256 JWT</span>
            </div>
            <div className="account-summary-row">
              <span className="summary-label">Password Hash</span>
              <span className="summary-val">PBKDF2-SHA512</span>
            </div>
            <div className="account-summary-row">
              <span className="summary-label">Platform</span>
              <span className="summary-val">AHC V5 Assessment</span>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
